
import { Component, Vue, Watch } from "vue-property-decorator";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vselect3 from "vselect3";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";

import ConfigurationsList from "@/components/ccure/lockdown/ConfigurationsList.vue";
import StaleDataModal from "@/components/ccure/lockdown/StaleDataModal.vue";
import { ClearanceFilterLevels, LockdownConfiguration, LockdownConfigurationByDesignation, UpdatedLockdownClearanceByName } from '@/store/lockdown/types';
import { namespace, Mutation, Getter, State } from 'vuex-class';
import EnhancedMap from '@/components/EnhancedMap.vue';
import { Group } from '@/store/eventqueue/types';
import NavHeader from "@/components/NavHeader.vue";

import { GPSService } from '@/services/gps.service';
import { CCureResponse } from '@/services/api.ccure.service';
import CCureApi from '@/services/api.ccure.service';
import { AxiosResponse } from 'axios';
import { UserPermissions } from '@/store/types';

const Lockdown = namespace("lockdown");

@Component({
	components: {
		"area-select": AreaTreeSelect,
		"configurations-list": ConfigurationsList,
		"v-select-3": vselect3,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"enhanced-map": EnhancedMap,
        "nav-header": NavHeader,
		"stale-data-modal": StaleDataModal
	}
})
export default class CCureLockdown extends Vue {
	@Lockdown.State(state => state.lockdownConfiguration) private lockdownConfiguration: LockdownConfiguration;
	@Lockdown.State(state => state.lockdownConfigurations) private lockdownConfigurations: LockdownConfigurationByDesignation[];
	@Lockdown.State private selectedClearanceLevel: number;
	@Lockdown.Action private updateClearanceFilterLevel: (updatedLockdownDetailsByName: UpdatedLockdownClearanceByName[]) => Promise<boolean>;
	@Lockdown.Action private fetchLockdownConfiguration: (groupID: number) => Promise<void>;
	@Lockdown.Mutation private setUpdateReasonOnLockdownConfiguration:(reason: string) => void;
	@Lockdown.Mutation private setSelectedClearanceFilterLevel:(clearanceLevel: number) => void;

	@Getter getFeature: (featureNames: string[]) => boolean;
	@Getter getPermissions: UserPermissions;

	@State(State => State.User.requireReasonUpdatingClearanceOnReaderGroups) requireReason: boolean;
	@Mutation private setIsLoading: (loading: boolean) => void;

	readonly ccureDataRequestStatusSuccessful: number = 200;

	private confirmModalShown: boolean = false;
	private groupId: number = null;
	private groupName: string = "";
	private resultsShown: boolean = false;
	private selectedClearanceFilterLevel: number = null;
	private selectedGroup = null;
	private currentGroupSelected: Group = null
	private loadMap: boolean = false;
	private mapLoaded: boolean = false;
	private updateReason: string = "";
	private geoCoder: GPSService = new GPSService();
	private ccureShardInstalled: boolean = false;
	private activeTab: number = 1

	@Watch("selectedClearanceFilterLevel")
	public onSelectedClearanceFilterLevelChanged(newValue: number) {
		this.setSelectedClearanceFilterLevel(newValue);
	}

	@Watch("selectedGroup", { deep: true })
	public selectGroup(newSelectedGroup) {
		if (newSelectedGroup) {
			this.groupId = this.selectedGroup.id;
			this.groupName = this.selectedGroup.label;

			this.activeTab = 0;
		}
		
		this.resultsShown = false;
	}

	async mounted() {
		this.setIsLoading(true);
		this.selectedClearanceFilterLevel = null;
		if (this.lockdownFeatureEnabled && (this.lockdownViewPermissionEnabled || this.lockdownEditPermissionEnabled)) {
			this.ccureShardInstalled = await this.ccureShardIsInstalled();
		}
		this.setIsLoading(false);
	}

	public async ccureShardIsInstalled(): Promise<boolean> {
		let response: AxiosResponse<CCureResponse> = await CCureApi.testConnection(-1);
		if (response && response.data && response.status === undefined) {
			response = response.data as unknown as AxiosResponse<CCureResponse>;
		}

		if (response && response.status === 200 && response.data && response.data.successful) {
			return true;	
		}
		else {
			return false;		
		}
	}

	public get lockdownFeatureEnabled(): boolean {
		return this.getFeature(["Integration", "CCure", "UI", "Lockdown"]);
	}

	public get lockdownViewPermissionEnabled(): boolean {
		return this.getPermissions.canViewCCureLockdown;
	}

	public get lockdownEditPermissionEnabled(): boolean {
		return this.getPermissions.canEditCCureLockdown;
	}

	public get lockdownEnabled(): boolean {
		return this.lockdownFeatureEnabled && (this.lockdownViewPermissionEnabled || this.lockdownEditPermissionEnabled)  && this.ccureShardInstalled;
	}

	public get isReasonFieldEnabled():boolean {
		return this.getFeature(["Integration", "CCure", "UI", "Lockdown", "Reason"]);
	}

	private get isReasonProvided(): boolean {
		if (this.isReasonRequired && this.isReasonFieldEnabled) {
			if (!this.updateReason) {
				return false;
			}

			return this.updateReason.length > 0;
		}

		return true;
	}

	private get isReasonRequired(): boolean {
		return this.requireReason && this.isReasonFieldEnabled;
	}

	private get searchShouldBeDisabled(): boolean {
		return this.groupId === null
	}

	private get saveShouldBeDisabled(): boolean {
		if (!this.lockdownEditPermissionEnabled) {
			return true;
		}

		return !this.selectedClearanceLevel || !this.lockdownConfigurations || this.lockdownConfigurations.length === 0
	}

	private get clearanceFilterLevels(): number[] {
		return ClearanceFilterLevels;
	}

	private get confirmationQuestion(): string {
		return `Are you sure you want to set the clearance filter level to level ${
			this.selectedClearanceLevel
		} for all servers for ${this.groupName}?`;
	}

	private async handleSearch(): Promise<void> {
		this.setIsLoading(true);
		
		try {
			await this.fetchLockdownConfiguration(this.groupId);
			this.selectedClearanceFilterLevel = null;
		}
		catch {
			this.selectedGroup = null;

			this.$notify(
				{ type: "error", title: "Error", text: "Error getting lockdown configurations, please try again later." });
			return; 
		}
		finally {
			this.setIsLoading(false);
		}

		this.resultsShown = true;
		this.selectedClearanceFilterLevel = null;

		if (!this.mapLoaded) {
			this.loadMap = true;
			this.mapLoaded = true;
		} 
	}

	private handleSaveClick(): void {
		this.confirmModalShown = true;
	}

	private async handleConfirmClick(): Promise<void> {
		this.setIsLoading(true);

		try {
			if (this.isReasonRequired && !this.isReasonProvided) {
				return;
			} else if (this.isReasonRequired || (this.isReasonFieldEnabled && this.updateReason)) {
				this.setUpdateReasonOnLockdownConfiguration(this.updateReason);
			}

			let updatedLockdownDetailsByName = this.lockdownConfigurations.map((config: LockdownConfigurationByDesignation) => {
				return {
					name: config.name,
					level: this.selectedClearanceLevel
				}
			});

			const result = await this.updateClearanceFilterLevel(updatedLockdownDetailsByName);

			if (result) {
				this.selectedClearanceFilterLevel = null;
				this.$notify({
					type: "success",
					title: "Clearance Filter Level Updated",
					text: "Success"
				});
			}
		} catch {
			this.$notify({
				type: "error",
				title: "Error",
				text: "Unable to update the clearance filter levels"
			});
		} finally {
			this.confirmModalShown = false;
			this.updateReason = "";
			this.setUpdateReasonOnLockdownConfiguration("");
			this.setIsLoading(false);
		}
	}

	private handleConfirmModalHidden(): void {
		this.updateReason = "";
		this.setUpdateReasonOnLockdownConfiguration("");
		this.confirmModalShown = false;
	}

	private onMapTabSelected(): void {
		this.loadMap = false

		this.currentGroupSelected = {
			groupID: this.selectedGroup.id,
			title: this.selectedGroup.label,
			address: this.selectedGroup.address,
		}
	}

	private get showClearanceUpdateSection(): boolean {
		const configurationsExist = this.lockdownConfigurations?.length > 0
		return this.resultsShown && configurationsExist && this.lockdownEditPermissionEnabled;
	}
}
